import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Check, Square, Triangle, Circle, Minus, Undo2, Camera, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Normalize unit string to canonical form for calculations.
 * CRITICAL: "m" alone = metro lineal (ml), NOT metro cuadrado
 */
const normalizeUnit = (unitStr) => {
  if (!unitStr) return 'm²'; // Default
  
  const unit = unitStr.trim().toLowerCase();
  
  // m³-km variations (check first as it contains "m3")
  if (['m3-km', 'm3km', 'm³-km', 'm³km'].includes(unit)) return 'm3-km';
  
  // m² variations (metros cuadrados)
  if (['m²', 'm2', 'metros cuadrados', 'metro cuadrado', 'mts2', 'mt2'].includes(unit)) return 'm²';
  
  // m³ variations (metros cúbicos)
  if (['m³', 'm3', 'metros cubicos', 'metros cúbicos', 'metro cubico', 'metro cúbico', 'mts3', 'mt3'].includes(unit)) return 'm³';
  
  // ml variations (metros lineales) - CRITICAL: "m" alone = ml
  if (['ml', 'm', 'metro', 'metros', 'metro lineal', 'metros lineales', 'mt', 'mts'].includes(unit)) return 'ml';
  
  // pza variations (piezas)
  if (['pza', 'pz', 'pieza', 'piezas', 'pzas', 'unidad', 'unidades', 'und', 'u'].includes(unit)) return 'pza';
  
  // kg variations (kilogramos)
  if (['kg', 'kilogramo', 'kilogramos', 'kilo', 'kilos'].includes(unit)) return 'kg';
  
  // ton variations (toneladas)
  if (['ton', 'tonelada', 'toneladas', 'tn', 't'].includes(unit)) return 'ton';
  
  // litros variations
  if (['litro', 'litros', 'lt', 'lto', 'lts', 'l'].includes(unit)) return 'lt';
  
  return unitStr; // Return as-is if no match
};

export const MeasurementForm = ({ 
  unit, 
  figureNumber, 
  isDeduction,
  setIsDeduction,
  isDeductionLocked = false,
  captureState,
  CAPTURE_STATES,
  selectedShapeType,
  onShapeSelect,
  onConfirmShape,
  onAddFigure,
  onUndo,
  onMeasurementsChange,
  onPhotoComplete,
  onNewPhoto,
  onChangeConcept,
  onPinAdded,
  figures = [],
  pinCount = 0,
  hasPhoto = false,
  canUndo = false,
  calibrationStatus = null,  // GenerIA calibration card detection status
}) => {
  // Common fields
  const [ubicacion, setUbicacion] = useState('');
  const [calculatedQuantity, setCalculatedQuantity] = useState(0);
  
  // m² fields
  const [largo, setLargo] = useState('');
  const [alto, setAlto] = useState('');
  
  // m³ additional field
  const [profundidad, setProfundidad] = useState('');
  
  // Triangle/Trapezoid fields
  const [base, setBase] = useState('');
  const [altura, setAltura] = useState('');
  const [baseMayor, setBaseMayor] = useState('');
  const [baseMenor, setBaseMenor] = useState('');
  
  // Circle field
  const [diametro, setDiametro] = useState('');
  
  // ml field
  const [longitud, setLongitud] = useState('');
  
  // kg/ton fields - DISABLED FOR NOW
  const [tipoAcero, setTipoAcero] = useState('');
  const [dimensionAcero, setDimensionAcero] = useState('');
  const [longitudAcero, setLongitudAcero] = useState('');
  const [piezas, setPiezas] = useState('');
  const [pesoUnitario, setPesoUnitario] = useState(0);
  const [materials, setMaterials] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [filteredDimensions, setFilteredDimensions] = useState([]);

  // State checks
  const isFormEnabled = captureState === CAPTURE_STATES.SHAPE_CONFIRMED;
  const isShapeSelected = captureState === CAPTURE_STATES.SHAPE_SELECTED;
  const isPhotoComplete = captureState === CAPTURE_STATES.PHOTO_COMPLETE;
  const canSelectShape = captureState === CAPTURE_STATES.PHOTO_LOADED;
  
  // Normalize unit for mode detection
  const normalizedUnit = normalizeUnit(unit);
  const isPinMode = normalizedUnit === 'pza';
  const isSteelMode = normalizedUnit === 'kg' || normalizedUnit === 'ton';

  // Load materials for kg/ton - DISABLED
  useEffect(() => {
    // Steel mode disabled for now
    // if (isSteelMode) {
    //   fetchMaterials();
    // }
  }, [isSteelMode]);

  const fetchMaterials = async () => {
    try {
      const [matsRes, typesRes] = await Promise.all([
        axios.get(`${API}/materials`),
        axios.get(`${API}/materials/types`)
      ]);
      setMaterials(matsRes.data);
      setMaterialTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  // Filter dimensions when tipo changes
  useEffect(() => {
    if (tipoAcero) {
      const filtered = materials.filter(m => m.tipo === tipoAcero);
      setFilteredDimensions(filtered);
      setDimensionAcero('');
      setPesoUnitario(0);
    }
  }, [tipoAcero, materials]);

  // Set peso unitario when dimension changes
  useEffect(() => {
    if (dimensionAcero && tipoAcero) {
      const mat = materials.find(m => m.tipo === tipoAcero && m.dimension === dimensionAcero);
      if (mat) {
        setPesoUnitario(mat.peso_unitario);
      }
    }
  }, [dimensionAcero, tipoAcero, materials]);

  // Calculate quantity based on unit and shape
  useEffect(() => {
    let qty = 0;
    
    // Use normalized unit for calculation
    const normUnit = normalizeUnit(unit);
    const isArea = normUnit === 'm²';
    const isVolume = normUnit === 'm³';
    const isLinear = normUnit === 'ml';
    const isPin = normUnit === 'pza';
    const isSteel = normUnit === 'kg' || normUnit === 'ton';
    const isLiter = normUnit === 'lt';
    const isM3Km = normUnit === 'm3-km';
    
    if (isArea) {
      switch (selectedShapeType) {
        case 'rectangle':
          if (largo && alto) qty = parseFloat(largo) * parseFloat(alto);
          break;
        case 'circle':
          if (diametro) {
            const r = parseFloat(diametro) / 2;
            qty = Math.PI * r * r;
          }
          break;
        case 'triangle':
          if (base && altura) qty = (parseFloat(base) * parseFloat(altura)) / 2;
          break;
        case 'trapezoid':
          if (baseMayor && baseMenor && altura) {
            qty = ((parseFloat(baseMayor) + parseFloat(baseMenor)) * parseFloat(altura)) / 2;
          }
          break;
        default:
          break;
      }
    } else if (isVolume) {
      switch (selectedShapeType) {
        case 'rectangle':
          if (largo && alto && profundidad) {
            qty = parseFloat(largo) * parseFloat(alto) * parseFloat(profundidad);
          }
          break;
        case 'circle':
          if (diametro && profundidad) {
            const r = parseFloat(diametro) / 2;
            qty = Math.PI * r * r * parseFloat(profundidad);
          }
          break;
        case 'triangle':
          if (base && altura && profundidad) {
            qty = (parseFloat(base) * parseFloat(altura) / 2) * parseFloat(profundidad);
          }
          break;
        case 'trapezoid':
          if (baseMayor && baseMenor && altura && profundidad) {
            qty = ((parseFloat(baseMayor) + parseFloat(baseMenor)) * parseFloat(altura) / 2) * parseFloat(profundidad);
          }
          break;
        default:
          break;
      }
    } else if (isLinear) {
      if (longitud) qty = parseFloat(longitud);
    } else if (isPin) {
      qty = pinCount;
    } else if (isSteel) {
      if (longitudAcero && piezas && pesoUnitario) {
        qty = parseFloat(longitudAcero) * parseInt(piezas) * pesoUnitario;
        if (normUnit === 'ton') qty = qty / 1000;
      }
    } else if (isLiter) {
      // Liters: calculate volume in m³ and multiply by 1000
      switch (selectedShapeType) {
        case 'rectangle':
          if (largo && alto && profundidad) {
            qty = parseFloat(largo) * parseFloat(alto) * parseFloat(profundidad) * 1000;
          }
          break;
        case 'circle':
          if (diametro && profundidad) {
            const r = parseFloat(diametro) / 2;
            qty = Math.PI * r * r * parseFloat(profundidad) * 1000;
          }
          break;
        default:
          break;
      }
    } else if (isM3Km) {
      // m³-km uses same calculation as volume
      switch (selectedShapeType) {
        case 'rectangle':
          if (largo && alto && profundidad) {
            qty = parseFloat(largo) * parseFloat(alto) * parseFloat(profundidad);
          }
          break;
        default:
          break;
      }
    }
    
    setCalculatedQuantity(qty);
  }, [unit, selectedShapeType, largo, alto, profundidad, diametro, base, altura, 
      baseMayor, baseMenor, longitud, longitudAcero, piezas, pesoUnitario, pinCount]);

  // Helper variables for unit checks (used in render logic) - use normalized unit
  const normUnitForRender = normalizeUnit(unit);
  const isAreaUnit = normUnitForRender === 'm²';
  const isVolumeUnit = normUnitForRender === 'm³' || normUnitForRender === 'lt' || normUnitForRender === 'm3-km';
  const isLinearUnit = normUnitForRender === 'ml';

  // Notify parent of measurements for dimension labels
  useEffect(() => {
    if (onMeasurementsChange && isFormEnabled) {
      onMeasurementsChange({ 
        largo, alto, profundidad, base, altura, baseMayor, baseMenor, diametro, longitud,
        shapeType: selectedShapeType, unit
      });
    }
  }, [largo, alto, profundidad, base, altura, baseMayor, baseMenor, diametro, longitud, 
      onMeasurementsChange, isFormEnabled, selectedShapeType, unit]);

  // Reset form when shape changes
  useEffect(() => {
    resetFormFields();
  }, [selectedShapeType]);

  const resetFormFields = () => {
    setUbicacion('');
    setLargo('');
    setAlto('');
    setProfundidad('');
    setBase('');
    setAltura('');
    setBaseMayor('');
    setBaseMenor('');
    setDiametro('');
    setLongitud('');
    setLongitudAcero('');
    setPiezas('');
    setCalculatedQuantity(0);
  };

  const handleSubmit = () => {
    if (calculatedQuantity === 0 && !isPinMode) return;
    if (isPinMode && pinCount === 0) return;

    const formData = {
      ubicacion,
      largo: largo || null,
      alto: alto || null,
      profundidad: profundidad || null,
      base: base || null,
      altura: altura || null,
      baseMayor: baseMayor || null,
      baseMenor: baseMenor || null,
      diametro: diametro || null,
      longitud: longitud || longitudAcero || null,
      piezas: piezas || (isPinMode ? pinCount : null),
      tipoAcero: tipoAcero || null,
      dimensionAcero: dimensionAcero || null,
      pesoUnitario: pesoUnitario || null,
      cantidad: calculatedQuantity,
    };

    onAddFigure(formData);
    resetFormFields();
    if (isSteelMode) {
      setTipoAcero('');
      setDimensionAcero('');
      setPesoUnitario(0);
    }
  };

  // Calculate subtotal
  const subtotal = figures.reduce((acc, fig) => {
    if (fig.tipo === 'suma') return acc + (fig.cantidad || 0);
    return acc - (fig.cantidad || 0);
  }, 0);

  // Get available shapes based on unit
  const getAvailableShapes = () => {
    if (isLinearUnit) {
      return [{ type: 'line', label: 'Línea', icon: Minus }];
    }
    if (isPinMode || isSteelMode) {
      return []; // No shapes for these
    }
    // m² and m³
    return [
      { type: 'rectangle', label: 'Rectángulo', icon: Square },
      { type: 'triangle', label: 'Triángulo', icon: Triangle },
      { type: 'trapezoid', label: 'Trapecio', icon: Square },
      { type: 'circle', label: 'Círculo', icon: Circle },
    ];
  };

  const shapeButtons = getAvailableShapes();

  // Get formula display
  const getFormulaDisplay = () => {
    if (isLinearUnit) return 'L';
    if (isPinMode) return 'Conteo';
    if (isSteelMode) return unit === 'ton' ? '(L × Pzas × Peso/m) ÷ 1000' : 'L × Pzas × Peso/m';
    
    if (isVolumeUnit) {
      switch (selectedShapeType) {
        case 'rectangle': return 'L × A × P';
        case 'circle': return 'π×(D/2)²×P';
        case 'triangle': return '(B×A÷2)×P';
        case 'trapezoid': return '((B1+B2)×A÷2)×P';
        default: return '';
      }
    }
    
    switch (selectedShapeType) {
      case 'rectangle': return 'L × A';
      case 'circle': return 'π × (D/2)²';
      case 'triangle': return 'B × A ÷ 2';
      case 'trapezoid': return '(B1+B2) × A ÷ 2';
      default: return '';
    }
  };

  // Render shape-specific fields
  const renderShapeFields = () => {
    const inputClasses = `mt-1 h-8 text-sm bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] ${!isFormEnabled ? 'opacity-60 cursor-not-allowed' : ''}`;

    // Steel mode (kg/ton) - DISABLED FOR NOW
    if (isSteelMode) {
      return (
        <div className="space-y-3">
          <div className="p-4 bg-[#FF6B00]/10 border border-[#FF6B00]/30 rounded-lg text-center">
            <p className="text-[#FF6B00] font-semibold text-sm mb-1">PRÓXIMAMENTE</p>
            <p className="text-[#5A7A9A] text-xs">
              La función de cálculo de acero estará disponible en la próxima versión.
            </p>
          </div>
        </div>
      );
    }

    // Pin mode (pza)
    if (isPinMode) {
      return (
        <div className="text-center py-4">
          <p className="text-[#5A7A9A] mb-2">Toca la foto para marcar cada elemento</p>
          <p className="text-3xl font-bold text-[#00CFFF]">{pinCount} piezas</p>
        </div>
      );
    }

    // ml mode - Longitud only
    if (isLinearUnit) {
      return (
        <div>
          <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Longitud (m)</Label>
          <Input
            type="number"
            step="0.01"
            value={longitud}
            onChange={(e) => setLongitud(e.target.value)}
            className={inputClasses}
            disabled={!isFormEnabled}
            data-testid="input-longitud"
          />
        </div>
      );
    }

    // Circle - Only diámetro
    if (selectedShapeType === 'circle') {
      return (
        <div className="space-y-2">
          <div>
            <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Diámetro (m)</Label>
            <Input
              type="number"
              step="0.01"
              value={diametro}
              onChange={(e) => setDiametro(e.target.value)}
              className={inputClasses}
              disabled={!isFormEnabled}
              data-testid="input-diametro"
            />
          </div>
          {isVolumeUnit && (
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Profundidad (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={profundidad}
                onChange={(e) => setProfundidad(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
                data-testid="input-profundidad"
              />
            </div>
          )}
        </div>
      );
    }

    // Rectangle
    if (selectedShapeType === 'rectangle') {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Largo (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={largo}
                onChange={(e) => setLargo(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
                data-testid="input-largo"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Alto (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={alto}
                onChange={(e) => setAlto(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
                data-testid="input-alto"
              />
            </div>
          </div>
          {isVolumeUnit && (
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Profundidad (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={profundidad}
                onChange={(e) => setProfundidad(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
                data-testid="input-profundidad"
              />
            </div>
          )}
        </div>
      );
    }

    // Triangle
    if (selectedShapeType === 'triangle') {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Base (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={base}
                onChange={(e) => setBase(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
                data-testid="input-base"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Altura (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={altura}
                onChange={(e) => setAltura(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
                data-testid="input-altura"
              />
            </div>
          </div>
          {isVolumeUnit && (
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Profundidad (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={profundidad}
                onChange={(e) => setProfundidad(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
                data-testid="input-profundidad"
              />
            </div>
          )}
        </div>
      );
    }

    // Trapezoid
    if (selectedShapeType === 'trapezoid') {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Base Mayor (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={baseMayor}
                onChange={(e) => setBaseMayor(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Base Menor (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={baseMenor}
                onChange={(e) => setBaseMenor(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Altura (m)</Label>
            <Input
              type="number"
              step="0.01"
              value={altura}
              onChange={(e) => setAltura(e.target.value)}
              className={inputClasses}
              disabled={!isFormEnabled}
            />
          </div>
          {isVolumeUnit && (
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Profundidad (m)</Label>
              <Input
                type="number"
                step="0.01"
                value={profundidad}
                onChange={(e) => setProfundidad(e.target.value)}
                className={inputClasses}
                disabled={!isFormEnabled}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="text-center text-[#5A7A9A] text-sm py-4">
        Selecciona un tipo de figura
      </div>
    );
  };

  // PHOTO COMPLETE STATE
  if (isPhotoComplete) {
    return (
      <div className="h-full flex flex-col bg-[#0D1520]" data-testid="measurement-form">
        <div className="flex-1 flex flex-col items-center justify-center gap-6 p-4">
          <div className="text-center mb-4">
            <Check className="w-16 h-16 text-[#22C55E] mx-auto mb-2" />
            <h3 className="text-xl font-bold text-[#E2EAF4]">FOTO LISTA</h3>
            <p className="text-[#5A7A9A] text-sm mt-1">
              {figures.length} figura{figures.length !== 1 ? 's' : ''} agregada{figures.length !== 1 ? 's' : ''}
            </p>
            {figures.length > 0 && (
              <p className="text-lg font-bold text-[#0066FF] mt-2">
                Total: {subtotal.toFixed(2)} {unit}
              </p>
            )}
          </div>
          
          <Button
            onClick={onNewPhoto}
            className="w-full h-12 bg-[#0066FF] hover:bg-[#0066FF]/80 text-white font-semibold"
            data-testid="new-photo-btn"
          >
            <Camera className="w-5 h-5 mr-2" />
            + NUEVA FOTO
          </Button>
          
          <Button
            onClick={onChangeConcept}
            variant="outline"
            className="w-full h-12 font-semibold border-2 border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF]/10"
            data-testid="change-concept-btn"
          >
            <ArrowRight className="w-5 h-5 mr-2" />
            CAMBIAR CONCEPTO
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#0D1520]" data-testid="measurement-form">
      <div className="flex-1 overflow-y-auto pr-1">
        {/* Figure Number */}
        <div className="bg-[#0A1422] p-3 rounded-lg border border-[#1E3550] mb-3">
          <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Figura No.</Label>
          <p className="text-xl font-bold mono text-[#0066FF]" data-testid="figure-number">
            {String(figureNumber).padStart(6, '0')}
          </p>
        </div>

        {/* SUMA / DEDUCCIÓN Toggle - Locks once shape is placed */}
        <div className="flex gap-2 mb-3">
          <Button
            type="button"
            variant={!isDeduction ? 'default' : 'outline'}
            className={`flex-1 h-9 font-semibold text-sm ${!isDeduction ? 'bg-[#22C55E] hover:bg-[#22C55E]/80 text-white' : 'border-[#1E3550] text-[#E2EAF4]'} ${isDeductionLocked ? 'opacity-60' : ''}`}
            onClick={() => !isDeductionLocked && setIsDeduction(false)}
            disabled={isDeductionLocked}
            data-testid="toggle-suma"
          >
            <Plus className="w-4 h-4 mr-1" />
            SUMA
          </Button>
          <Button
            type="button"
            variant={isDeduction ? 'default' : 'outline'}
            className={`flex-1 h-9 font-semibold text-sm ${isDeduction ? 'bg-red-600 hover:bg-red-700 text-white' : 'border-[#1E3550] text-[#E2EAF4]'} ${isDeductionLocked ? 'opacity-60' : ''}`}
            onClick={() => !isDeductionLocked && setIsDeduction(true)}
            disabled={isDeductionLocked}
            data-testid="toggle-deduccion"
          >
            <Minus className="w-4 h-4 mr-1" />
            DEDUCCIÓN
          </Button>
        </div>

        {/* Shape Buttons - Only for m²/m³/ml, NOT for kg/ton */}
        {shapeButtons.length > 0 && !isSteelMode && (
          <div className="mb-3">
            <Label className="text-xs uppercase font-semibold text-[#5A7A9A] mb-1 block">Tipo de Figura</Label>
            <div className={`grid gap-1 ${shapeButtons.length <= 2 ? 'grid-cols-2' : 'grid-cols-2'}`}>
              {shapeButtons.map((shape) => {
                const Icon = shape.icon;
                const isActive = selectedShapeType === shape.type;
                return (
                  <Button
                    key={shape.type}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    className={`h-9 text-xs font-medium ${!canSelectShape ? 'opacity-50 cursor-not-allowed' : ''} ${isActive ? 'bg-[#0066FF]' : 'border-[#1E3550] text-[#E2EAF4]'}`}
                    onClick={() => canSelectShape && onShapeSelect(shape.type)}
                    disabled={!canSelectShape}
                    data-testid={`shape-btn-${shape.type}`}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {shape.label}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* Pin mode instruction */}
        {isPinMode && canSelectShape && (
          <div className="mb-3 p-3 bg-[#FF6B00]/10 border border-[#FF6B00]/30 rounded-lg text-center">
            <p className="text-sm text-[#FF6B00] font-medium">
              Toca cada elemento en la foto para contarlo
            </p>
          </div>
        )}

        {/* Undo Button */}
        {canUndo && (
          <Button
            variant="outline"
            size="sm"
            onClick={onUndo}
            className="w-full h-8 mb-3 text-[#FF6B00] border-[#FF6B00]/30 hover:bg-[#FF6B00]/10"
            data-testid="undo-btn"
          >
            <Undo2 className="w-4 h-4 mr-1" />
            Deshacer
          </Button>
        )}

        {/* CONFIRMAR FIGURA Button */}
        {isShapeSelected && !isPinMode && (
          <Button
            onClick={onConfirmShape}
            className="w-full h-10 mb-3 bg-[#0066FF] hover:bg-[#0066FF]/80 text-white font-semibold"
            data-testid="confirm-shape-btn"
          >
            <Check className="w-5 h-5 mr-2" />
            CONFIRMAR FIGURA
          </Button>
        )}

        {/* Form Fields */}
        <div className="space-y-2 mb-3">
          {/* GenerIA Calibration Card Status - Show only when shape is confirmed */}
          {isFormEnabled && !isPinMode && (
            <div 
              className={`p-2 rounded-lg border text-center text-sm ${
                calibrationStatus?.regla_detected 
                  ? 'bg-[#22C55E]/10 border-[#22C55E]/50 text-[#22C55E]'
                  : calibrationStatus === null
                    ? 'bg-[#0066FF]/10 border-[#0066FF]/30 text-[#5A7A9A]'
                    : 'bg-[#FF6B00]/10 border-[#FF6B00]/30 text-[#FF6B00]'
              }`}
              data-testid="calibration-status"
            >
              {calibrationStatus === null ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-3 h-3 border-2 border-[#0066FF] border-t-transparent rounded-full animate-spin"></span>
                  Detectando regla GenerIA...
                </span>
              ) : calibrationStatus.regla_detected ? (
                <span>
                  Regla GenerIA detectada ✅ — {calibrationStatus.reference_mm / 10}cm de referencia
                </span>
              ) : (
                <span>Sin regla — estimación IA</span>
              )}
            </div>
          )}
          
          {!isPinMode && (
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Ubicación</Label>
              <Input
                type="text"
                value={ubicacion}
                onChange={(e) => setUbicacion(e.target.value)}
                placeholder="Ej: Eje A-B, Nivel 1"
                className={`mt-1 h-8 text-sm bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A] ${!isFormEnabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                disabled={!isFormEnabled}
                data-testid="input-ubicacion"
              />
            </div>
          )}
          
          {renderShapeFields()}
          
          {/* Calculated Quantity - Show more decimals for small values */}
          <div className={`p-2 rounded-lg border-2 ${isFormEnabled || isPinMode ? 'bg-[#0066FF]/10 border-[#0066FF]' : 'bg-[#0A1422] border-[#1E3550]'}`}>
            <div className="flex justify-between items-center">
              <Label className="text-xs uppercase font-semibold text-[#5A7A9A]">Cantidad</Label>
              {getFormulaDisplay() && (
                <span className="text-xs text-[#5A7A9A] mono">{getFormulaDisplay()}</span>
              )}
            </div>
            <p className="text-xl font-bold mono text-[#0066FF]" data-testid="calculated-quantity">
              {calculatedQuantity < 0.01 && calculatedQuantity > 0 
                ? calculatedQuantity.toFixed(4) 
                : calculatedQuantity.toFixed(2)} {unit}
            </p>
          </div>
        </div>

        {/* GUARDAR FIGURA Button */}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={(!isFormEnabled && !isPinMode) || (calculatedQuantity === 0 && !isPinMode) || (isPinMode && pinCount === 0) || isSteelMode}
          className={`w-full h-10 mb-3 font-semibold ${
            ((isFormEnabled && calculatedQuantity > 0) || (isPinMode && pinCount > 0)) && !isSteelMode
              ? 'bg-[#22C55E] hover:bg-[#22C55E]/80 text-white' 
              : 'bg-[#1E3550] text-[#5A7A9A] cursor-not-allowed'
          }`}
          data-testid="add-figure-button"
        >
          <Plus className="w-5 h-5 mr-2" />
          GUARDAR FIGURA
        </Button>

        {/* Figure List - Scrollable with fixed SUBTOTAL */}
        {figures.length > 0 && (
          <div className="mb-3 flex flex-col" style={{ maxHeight: '180px' }}>
            <Label className="text-xs uppercase font-semibold text-[#5A7A9A] mb-1 block">
              Figuras ({figures.length})
            </Label>
            <div className="border border-[#1E3550] rounded-lg bg-[#0A1422] flex flex-col overflow-hidden flex-1">
              {/* Scrollable list */}
              <div className="overflow-y-auto flex-1" style={{ maxHeight: '120px' }}>
                <table className="w-full text-xs">
                  <tbody>
                    {figures.map((fig, idx) => {
                      // Format dimensions with prefixes and full math operation
                      let dimensionStr = '';
                      const cantidad = fig.cantidad || 0;
                      const cantidadFmt = cantidad < 0.01 && cantidad > 0 
                        ? cantidad.toFixed(4) 
                        : cantidad.toFixed(2);
                      
                      if (fig.largo && fig.alto && fig.profundidad) {
                        // m³ rectangle: L:0.30×A:0.45×Prof:0.30=0.04
                        const l = parseFloat(fig.largo).toFixed(2);
                        const a = parseFloat(fig.alto).toFixed(2);
                        const p = parseFloat(fig.profundidad).toFixed(2);
                        dimensionStr = `L:${l}×A:${a}×Prof:${p}=${cantidadFmt}`;
                      } else if (fig.largo && fig.alto) {
                        // m²: L:2.90×A:2.60=7.54
                        const l = parseFloat(fig.largo).toFixed(2);
                        const a = parseFloat(fig.alto).toFixed(2);
                        dimensionStr = `L:${l}×A:${a}=${cantidadFmt}`;
                      } else if (fig.longitud) {
                        // ml: L:3.30
                        dimensionStr = `L:${parseFloat(fig.longitud).toFixed(2)}`;
                      } else if (fig.piezas !== undefined && fig.piezas !== null) {
                        // pza: 5 pines
                        dimensionStr = `${fig.piezas} pines`;
                      } else if (fig.diametro && fig.profundidad) {
                        // m³ circle: D:1.20×Prof:0.30=...
                        const d = parseFloat(fig.diametro).toFixed(2);
                        const p = parseFloat(fig.profundidad).toFixed(2);
                        dimensionStr = `D:${d}×Prof:${p}=${cantidadFmt}`;
                      } else if (fig.diametro) {
                        // m² circle: D:1.20=...
                        const d = parseFloat(fig.diametro).toFixed(2);
                        dimensionStr = `D:${d}=${cantidadFmt}`;
                      } else if (fig.baseMayor && fig.baseMenor && fig.altura && fig.profundidad) {
                        // m³ trapezoid: (Bm:2.00+BM:3.00)×h:2.60÷2×Prof:0.30=...
                        const bm = parseFloat(fig.baseMenor).toFixed(2);
                        const bM = parseFloat(fig.baseMayor).toFixed(2);
                        const h = parseFloat(fig.altura).toFixed(2);
                        const p = parseFloat(fig.profundidad).toFixed(2);
                        dimensionStr = `(Bm:${bm}+BM:${bM})×h:${h}÷2×Prof:${p}=${cantidadFmt}`;
                      } else if (fig.baseMayor && fig.baseMenor && fig.altura) {
                        // m² trapezoid: (Bm:2.00+BM:3.00)×h:2.60÷2=...
                        const bm = parseFloat(fig.baseMenor).toFixed(2);
                        const bM = parseFloat(fig.baseMayor).toFixed(2);
                        const h = parseFloat(fig.altura).toFixed(2);
                        dimensionStr = `(Bm:${bm}+BM:${bM})×h:${h}÷2=${cantidadFmt}`;
                      } else if (fig.base && fig.altura && fig.profundidad) {
                        // m³ triangle: B:3.00×h:2.60÷2×Prof:0.30=...
                        const b = parseFloat(fig.base).toFixed(2);
                        const h = parseFloat(fig.altura).toFixed(2);
                        const p = parseFloat(fig.profundidad).toFixed(2);
                        dimensionStr = `B:${b}×h:${h}÷2×Prof:${p}=${cantidadFmt}`;
                      } else if (fig.base && fig.altura) {
                        // m² triangle: B:3.00×h:2.60÷2=...
                        const b = parseFloat(fig.base).toFixed(2);
                        const h = parseFloat(fig.altura).toFixed(2);
                        dimensionStr = `B:${b}×h:${h}÷2=${cantidadFmt}`;
                      }
                      
                      const isDeduccion = fig.tipo === 'deduccion';
                      
                      return (
                        <tr
                          key={fig.id || idx}
                          className={`border-b border-[#1E3550] ${isDeduccion ? 'bg-red-500/10' : ''}`}
                        >
                          <td className="py-1.5 px-2 font-mono font-semibold text-[#E2EAF4]" style={{ width: '70px' }}>
                            {String(fig.figure_number).padStart(6, '0')}
                          </td>
                          <td className="py-1.5 px-1 text-[#5A7A9A] truncate" style={{ maxWidth: '60px' }}>
                            {fig.ubicacion || '-'}
                          </td>
                          <td className="py-1.5 px-1 font-mono text-[#5A7A9A] text-center" style={{ fontSize: '10px', width: '90px' }}>
                            {dimensionStr}
                          </td>
                          <td className={`py-1.5 px-2 font-mono font-semibold text-right ${isDeduccion ? 'text-red-500' : 'text-[#22C55E]'}`} style={{ width: '80px' }}>
                            {isDeduccion ? '-' : '+'}{cantidadFmt}{unit}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Fixed SUBTOTAL row - always visible */}
              <div className="p-2 bg-[#0066FF] flex-shrink-0">
                <div className="flex justify-between items-center">
                  <span className="font-bold uppercase text-xs text-white">SUBTOTAL:</span>
                  <span className="font-mono font-bold text-white text-right" data-testid="subtotal" style={{ minWidth: '80px' }}>
                    {subtotal < 0.01 && subtotal > 0 ? subtotal.toFixed(4) : subtotal.toFixed(2)} {unit}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FOTO LISTA Button */}
      <div className="pt-3 border-t border-[#1E3550] mt-auto">
        <Button
          variant="outline"
          onClick={onPhotoComplete}
          className="w-full h-10 font-semibold border-2 border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF]/10"
          disabled={!hasPhoto}
          data-testid="photo-complete-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          FOTO LISTA
        </Button>
      </div>
    </div>
  );
};

export default MeasurementForm;
